import React, { useEffect, useRef, useState } from 'react';
import { PermissionsRequired } from '../../type/menu-type';
import { CheckPermissions } from '../../utils/CheckPermissions';
import CustomSelect from './CustomSelect';

interface OptionType {
    label: string;
    value: string;
}

interface Props {
    id: string;
    label: string;
    filters: any;
    minInputLength?: number;
    isDisabled?: boolean;
    updateFilters?: (filter: any) => void;
    fetchOptions: (searchText: string | null) => Promise<OptionType[]>;
    getOptions?: () => any;
    permissions?: PermissionsRequired;
    containerClassName?: string;
}

const CustomSearchableEntities: React.FC<Props> = ({
    id,
    label,
    filters,
    minInputLength = 2,
    isDisabled = false,
    permissions,
    containerClassName,
    getOptions,
    updateFilters,
    fetchOptions
}) => {
    const [options, setOptions] = useState<OptionType[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [filtersValue, setFiltersValue] = useState<OptionType[]>([]);
    const [loading, setLoading] = useState(false);
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

    const handleInputChange = (value: string) => {
        setInputValue(value);

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        if (value.length >= minInputLength) {
            setLoading(true);
            debounceTimeout.current = setTimeout(() => {
                fetchOptions(value)
                    .then(fetchedOptions => {
                        setOptions(fetchedOptions);
                    })
                    .catch(error => {
                        console.error('Error fetching options:', error);
                        setOptions([]);
                    })
                    .finally(() => setLoading(false));
            }, 1000);
        } else {
            setOptions([]);
        }
    };

    const handleFiltersValue = (valueArray: OptionType[]) => {
        if (valueArray && valueArray.length > 0) {
            const values = valueArray.map((item) => item.value);
            updateFilters && updateFilters({ [id]: values });
            setFiltersValue(valueArray);
        } else {
            updateFilters && updateFilters({ [id]: [] });
            setFiltersValue([]);
        }
    };

    // Cargar valores por defecto desde los filtros
    useEffect(() => {
        const loadDefaultValues = async () => {
            if (filters.filter_filters?.[id] && filters.filter_filters[id].length > 0 && options.length === 0) {
                try {
                    setLoading(true);
                    const defaultOptions = await fetchOptions(null);
                    setOptions(defaultOptions);

                    const selectedOptions = defaultOptions.filter(opt =>
                        filters.filter_filters[id].includes(opt.value)
                    );
                    setFiltersValue(selectedOptions);
                } catch (error) {
                    console.error('Error loading default consultancies:', error);
                } finally {
                    setLoading(false);
                }
            }
        };

        loadDefaultValues();
    }, []);

    // Si se proporciona getOptions y hay filtros activos, precargar opciones
    useEffect(() => {
        if (getOptions && options.length === 0 && filters.filter_filters?.[id]) {
            const optionValues = getOptions();
            setOptions(optionValues);

            const selectedOptions = optionValues.filter((opt: any) =>
                filters.filter_filters[id].includes(opt.value)
            );
            setFiltersValue(selectedOptions);
        }
    }, []);

    // Reset de selección si se eliminan los filtros
    useEffect(() => {
        if (!filters.filter_filters?.[id]) setFiltersValue([]);
    }, [filters]);

    return (
        <>
            {(!permissions || (permissions && CheckPermissions(permissions))) && (
                <CustomSelect
                    id={id}
                    label={label}
                    isSearchable
                    isClearable
                    isMulti
                    isDisabled={isDisabled}
                    options={options}
                    isLoading={loading}
                    onInputChange={handleInputChange}
                    onChange={handleFiltersValue}
                    inputValue={inputValue}
                    value={filtersValue}
                    noOption={() =>
                        inputValue.length < minInputLength
                            ? `Introduce al menos ${minInputLength} letras`
                            : 'No se encontraron opciones'
                    }
                    containerClassName={containerClassName}
                />
            )}
        </>
    );
};

export default CustomSearchableEntities;